import { cond, equals, map } from 'ramda';
import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn from '@ui/atoms/FlexColumn';
import FlexRow, { IFlexRow } from '@ui/atoms/FlexRow';
import HeadingText from '@ui/atoms/HeadingText';
import SubheadingText from '@ui/atoms/SubheadingText';
import Tooltip, { ITooltipProps } from '@ui/atoms/Tooltip';

import aligned from '@ui/styled/Aligned';
import padded from '@ui/styled/Padded';

import { VerticalPositionTypes } from '@folx-types/theme';

import theme from '@theme/FolxTheme';

type TitleTypes = 'field' | 'subheading' | 'heading';

export interface INewInputWrapperProps {
	title: string | string[];
	children?: ReactNode;
	className?: string;
	groupName?: string;
	subtitle?: string;
	titleType?: TitleTypes;
	tooltipTitle?: string;
	tooltipDescription?: string;
	error?: string | boolean;
	errorPos?: VerticalPositionTypes;
}

const AlignedRow = aligned<IFlexRow>(FlexRow);
const PaddedTooltip = padded<ITooltipProps>(Tooltip);

const BottomErrorWrapper = styled.div`
	margin-top: ${theme.spacing.md};
`;

const NewInputWrapper: FC<INewInputWrapperProps> = ({
	children,
	className,
	error,
	errorPos = 'top',
	groupName,
	subtitle,
	title,
	titleType = 'subheading',
	tooltipTitle,
	tooltipDescription,
}) => {
	const Title = cond<TitleTypes, ReactNode>([
		[equals<TitleTypes>('field'), () => BasicText],
		[equals<TitleTypes>('subheading'), () => SubheadingText],
		[equals<TitleTypes>('heading'), () => HeadingText],
	])(titleType);

	const renderedTitle = () => {
		if (typeof title === 'string') {
			return <Title variant="field">{title}</Title>;
		}

		if (title === null) console.warn('Title was null, debug Butter if not intentional');

		return map((t: string) => <Title variant="field">{t}</Title>)(title);
	};

	const input = (
		<>
			<FlexColumn gap="sm" className={className}>
				<AlignedRow alignItems="center" gap="xs">
					{renderedTitle()}
					{tooltipTitle && (
						<PaddedTooltip
							defaultIcon
							fillColor="yellow"
							title={tooltipTitle}
							description={tooltipDescription}
							placement="top"
							showArrow
							padding="none"
						/>
					)}
				</AlignedRow>
				{!!subtitle && <BasicText>{subtitle}</BasicText>}
				{!!error && errorPos === 'top' && (
					<BasicText color="red" tint="darken40">
						{error}
					</BasicText>
				)}
			</FlexColumn>
			{children}
			{!!error && errorPos === 'bottom' && (
				<BottomErrorWrapper>
					<BasicText color="red" tint="darken40">
						{error}
					</BasicText>
				</BottomErrorWrapper>
			)}
		</>
	);
	return groupName ? <label htmlFor={groupName}>{input}</label> : input;
};

export default styled(NewInputWrapper)``;
